<template>
  <div class="resources__container">
    <main class="resources__main">
      <sidebar
        class="resources__sidebar"
        :sidebarList="getCategories"
        :activatedTabIdx="activatedCategoryIdx"
        @selected="selectCategory"
      />
      <div class="resources__page-wrapper">
        <keep-alive>
          <component :is="contentTemplate" :key="cPage.sys.id" :cPage="cPage" />
        </keep-alive>
      </div>
    </main>
  </div>
</template>
<script>
import routeUtils from '@/lib/utils/routes'

import { useContentfulComponent } from '@/components/common/contentful/composables/useContentfulComponent'
import Sidebar from '@/components/common/Sidebar.vue'

import Articles from '@/components/resources/pages/Articles.vue'
import Calendar from '@/components/resources/pages/Calendar.vue'
import EducationalContent from '@/components/resources/pages/EducationalContent.vue'
import EventDetails from '@/components/resources/pages/EventDetails.vue'
import EventSummary from '@/components/resources/pages/EventSummary.vue'
import ImagedList from '@/components/resources/pages/ImagedList.vue'
import Instructions from '@/components/resources/pages/Instructions.vue'
import Newsletter from '@/components/resources/pages/Newsletter.vue'
import Posters from '@/components/resources/pages/Posters.vue'
import ProductCategories from '@/components/resources/pages/ProductCategories.vue'
import ReleaseDetails from '@/components/resources/pages/ReleaseDetails.vue'
import ReleaseSummary from '@/components/resources/pages/ReleaseSummary.vue'

import { PAGETYPES } from '@/settings/constants'

export default {
  name: 'Resources',
  components: {
    Sidebar,
    Instructions,
    Articles,
    Posters,
    ImagedList,
    Calendar,
    Newsletter,
    ReleaseDetails,
    EventDetails,
    EventSummary,
    ReleaseSummary,
    EducationalContent,
    ProductCategories,
  },
  props: {
    catalog: {
      type: Object,
      required: true,
    },
    cPage: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { fetchItems } = useContentfulComponent()
    return {
      fetchItems,
    }
  },
  data() {
    return {
      activatedCategoryIdx: null,
      categories: this.catalog.subpages || [],
      metaData: {
        title: '',
        meta: [],
      },
    }
  },
  head() {
    const metaInfo = {}
    if (this.metaData.title) metaInfo.title = this.metaData.title
    if (this.metaData.metaDescription) {
      metaInfo.meta = [{ name: 'description', content: this.metaData.metaDescription || '' }]
    }
    return metaInfo
  },
  computed: {
    page() {
      return this.cPage.fields
    },
    getCategories() {
      return this.categories?.map((el) => el.fields) || []
    },
    contentTemplate() {
      switch (this.page.pageType) {
        case PAGETYPES.CATEGORY_RESOURCE_EVENTS:
          return EventSummary
        case PAGETYPES.CATEGORY_RESOURCE_PRESS_RELEASES:
          return ReleaseSummary
        case PAGETYPES.CATEGORY_RESOURCE_INVENTORY:
          return ImagedList
        case PAGETYPES.CATEGORY_RESOURCE_ISOLATION_CALENDAR:
          return Calendar
        case PAGETYPES.CATEGORY_RESOURCE_NEWSLETTER:
          return Newsletter
        case PAGETYPES.CATEGORY_RESOURCE_EDUCATIONAL_CONTENT:
          return EducationalContent
        case PAGETYPES.CATEGORY_RESOURCE_PRODUCTS:
          return ProductCategories
        case PAGETYPES.CATEGORY_RESOURCE_ARTICLES:
        default:
          return Articles
      }
    },
  },
  mounted() {
    this.setCategoryIdx()
    this.setPageMeta()
  },
  methods: {
    selectCategory(idx) {
      this.$router.push(this.categories[idx].link)
      this.setCategoryIdx(idx)
    },
    setCategoryIdx() {
      const slug = routeUtils.getSlugFromPathMatch(this.$route.params.pathMatch)
      let newIdx = this.categories.map((el) => el.fields.slug).indexOf(slug)
      if (newIdx === -1) {
        const categoryPage = this.$contentful.service
          .getPageBySlug(slug)
          ?.crumbs.find((crumb) =>
            this.categories.find((category) => category.fields.slug === crumb.fields.slug),
          )
        if (categoryPage)
          newIdx = this.categories.map((el) => el.fields.slug).indexOf(categoryPage.fields.slug)
      }

      this.activatedCategoryIdx = newIdx
    },
    async setPageMeta() {
      let cPageMeta = this.cPage?.fields?.pageMeta?.fields || {}
      if (this.cPage?.fields?.pageMeta && !this.cPage?.fields?.pageMeta?.fields) {
        const entryId = this.cPage?.sys?.id
        const entries = await this.fetchItems({
          skip: 0,
          limit: 1,
          include: 4,
          'sys.id': entryId,
        })
        const entry = entries[0]
        if (entry) {
          cPageMeta = entry.fields?.pageMeta?.fields || {}
        }
      }
      this.metaData = {
        title: cPageMeta.title,
        metaDescription: cPageMeta.metaDescription,
      }
    },
  },
}
</script>
